import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { X, User, Receipt, Users, LogOut, HelpCircle, Building, Wrench, Calendar, MessageSquare, AlertTriangle, Home, Megaphone, CalendarDays, Package, BarChart3, Wallet, Book, ClipboardCheck, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const MobileDrawer = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  const handleNavClick = (to) => {
    navigate(to);
    onClose();
  };

  const getInitials = () => {
    if (!user) return 'JD';
    return `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase() || 'JD';
  };

  const getFullName = () => {
    if (!user) return 'John Doe';
    const fullName = `${user.prenom || ''} ${user.nom || ''}`.trim();
    return fullName || 'Utilisateur';
  };

  // Menu items based on user role
  const getMenuItems = () => {
    const baseItems = [
      { to: '/', icon: Home, label: 'Accueil' },
      { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
      { to: '/messagerie', icon: MessageSquare, label: 'Messagerie' },
      { to: '/incidents', icon: AlertTriangle, label: 'Incidents' },
    ];

    if (user?.role === 'prestataire') {
      return [
        { to: '/', icon: Home, label: 'Accueil' },
        { to: '/messagerie', icon: MessageSquare, label: 'Messagerie' },
        { to: '/incidents', icon: AlertTriangle, label: 'Incidents' },
      ];
    }

    const commonItems = [
      ...baseItems,
      { to: '/assemblees', icon: Calendar, label: 'Assemblées générales' },
      { to: '/evenements', icon: CalendarDays, label: 'Événements' },
      { to: '/annonces', icon: Megaphone, label: 'Annonces' },
    ];

    // Admin and superadmin menu
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      return [
        ...commonItems,
        { divider: true, label: 'Gestion' },
        { to: '/immeubles', icon: Building, label: 'Mes Immeubles' },
        { to: '/coproprietaires', icon: Users, label: 'Mes Copropriétaires' },
        { to: '/equipe', icon: Users, label: 'Mon Équipe' },
        { to: '/prestataires', icon: Wrench, label: 'Mes Prestataires' },
        { to: '/visites', icon: ClipboardCheck, label: 'Visites d\'immeuble' },
        { to: '/carnet-entretien', icon: Book, label: 'Carnet d\'entretien' },
        { divider: true, label: 'Administration' },
        { to: '/admin', icon: BarChart3, label: 'Dashboard' },
        { to: '/admin/cabinets', icon: Building, label: 'Cabinets' },
        { to: '/admin/produits', icon: Package, label: 'Produits' },
        { to: '/admin/commandes', icon: Receipt, label: 'Commandes' },
        { to: '/admin/utilisateurs', icon: Users, label: 'Utilisateurs' },
      ];
    }

    // Gestionnaire menu
    if (user?.role === 'gestionnaire') {
      return [
        ...commonItems,
        { divider: true, label: 'Gestion' },
        { to: '/immeubles', icon: Building, label: 'Mes Immeubles' },
        { to: '/coproprietaires', icon: Users, label: 'Mes Copropriétaires' },
        { to: '/equipe', icon: Users, label: 'Mon Équipe' },
        { to: '/prestataires', icon: Wrench, label: 'Mes Prestataires' },
        { to: '/visites', icon: ClipboardCheck, label: 'Visites d\'immeuble' },
        { to: '/carnet-entretien', icon: Book, label: 'Carnet d\'entretien' },
      ];
    }

    // Directeur menu
    if (user?.role === 'directeur_cabinet') {
      return [
        ...commonItems,
        { divider: true, label: 'Gestion' },
        { to: '/immeubles', icon: Building, label: 'Mes Immeubles' },
        { to: '/coproprietaires', icon: Users, label: 'Mes Copropriétaires' },
        { to: '/equipe', icon: Users, label: 'Mon Équipe' },
        { to: '/prestataires', icon: Wrench, label: 'Mes Prestataires' },
        { divider: true, label: 'Administration' },
        { to: '/admin/utilisateurs', icon: Users, label: 'Gestion Utilisateurs' },
      ];
    }

    return commonItems;
  };

  const accountItems = [
    { to: '/mon-compte', icon: User, label: 'Mon compte' },
    // Factures masquées temporairement - à réactiver plus tard
    // { to: '/mes-factures', icon: Receipt, label: 'Mes Factures' },
  ];

  if (user?.role === 'coproprietaire') {
    accountItems.push({ to: '/sous-comptes', icon: Users, label: 'Sous comptes' });
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 lg:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 lg:hidden transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-drawer"
      >
        {/* Header with user info - Fixed at top */}
        <div className="flex-shrink-0 p-6 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Avatar and name */}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-blue-100 text-blue-600 font-semibold text-xl mb-3">
              {user?.photo_url ? (
                <img 
                  src={user.photo_url} 
                  alt="Photo de profil" 
                  className="w-full h-full object-cover"
                />
              ) : (
                getInitials()
              )}
            </div>
            <p className="font-semibold text-gray-800">{getFullName()}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.role || 'Utilisateur'}</p>
          </div>
        </div>

        {/* Navigation - Scrollable area with touch support */}
        <div 
          className="flex-1 overflow-y-auto py-4 overscroll-contain"
          style={{ 
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y'
          }}
        >
          {/* Account items */}
          <div className="px-4 mb-4">
            {accountItems.map((item) => (
              <button
                key={item.to}
                onClick={() => handleNavClick(item.to)}
                className="w-full flex items-center space-x-3 px-3 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors active:bg-gray-100"
              >
                <item.icon size={20} className="text-gray-500" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 my-2" />

          {/* Main navigation */}
          <div className="px-4">
            {getMenuItems().map((item, index) => {
              if (item.divider) {
                return (
                  <div key={index} className="py-2 px-3">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      {item.label}
                    </span>
                  </div>
                );
              }
              
              return (
                <button
                  key={item.to}
                  onClick={() => handleNavClick(item.to)}
                  className="w-full flex items-center space-x-3 px-3 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors active:bg-gray-100"
                >
                  <item.icon size={20} className="text-gray-500" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
          
          {/* Extra padding at bottom for better scroll experience */}
          <div className="h-4" />
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-gray-100 p-4 space-y-2">
          <button
            onClick={() => handleNavClick('/aide')}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-[#00D3A8]/10 hover:bg-[#00D3A8]/20 text-[#00D3A8] rounded-xl transition-colors font-medium text-sm"
          >
            <HelpCircle size={18} />
            <span>Centre d'aide</span>
          </button>
          <button
            onClick={() => handleNavClick('/glossaire')}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors text-xs"
          >
            <BookOpen size={14} />
            <span>Glossaire</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 rounded-xl transition-colors font-medium text-sm"
            data-testid="mobile-logout-btn"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileDrawer;
